import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { PortalShell, ProjectCard, StatusBadge } from "@/components/business/B2BPortal";
import { SEOHead } from "@/components/shared/SEOHead";
import {
  isBusinessPortalAuthError,
  productByIdFromSnapshot,
  quoteForProjectFromSnapshot,
  useBusinessPortalSnapshot,
} from "@/lib/b2bPortalApi";

export const Route = createFileRoute("/business/portal")({
  component: BusinessPortalDashboard,
});

function BusinessPortalDashboard() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const portal = useBusinessPortalSnapshot();
  const { account, brandAssets, catalog, projects, quotes, orders } = portal.data;

  if (pathname !== "/business/portal") {
    return <Outlet />;
  }

  const activeProjects = projects
    .filter((project) => project.status !== "shipped")
    .slice(0, 3);
  const recentOrders = orders.slice(0, 4);
  const quoteReviewCount = quotes.filter((quote) => quote.status === "sent").length;
  const portalTitle = account?.companyName ? `${account.companyName} portal` : "Packaging portal";

  return (
    <PortalShell
      active="/business/portal"
      eyebrow="Dashboard"
      title={portalTitle}
      description="Start orders, review proofs, and track production."
      account={account}
      actions={(
        <>
          <button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>
            Refresh
          </button>
          <Link to="/business/portal/projects/new" className="b2b-primary-button">
            Start order
          </Link>
        </>
      )}
    >
      <SEOHead title="Business Portal" description="Manage your Quuil packaging catalog, proofs, quotes, and orders." path="/business/portal" robots="noindex,follow" />

      {portal.demo && <DemoNotice />}
      {portal.error && !portal.demo ? (
        isBusinessPortalAuthError(portal.error) ? (
          <EmptyState
            title="Sign in to continue"
            detail="Your business workspace is protected by a tenant session."
            action={<Link to="/business/portal/login" className="b2b-primary-button">Sign in</Link>}
          />
        ) : (
          <EmptyState
            title="Could not load workspace"
            detail="The portal could not reach the business workspace API."
            action={<button type="button" className="b2b-secondary-button" onClick={() => void portal.refresh()}>Try again</button>}
          />
        )
      ) : portal.loading ? (
        <LoadingGrid />
      ) : (
        <>
          <section className="b2b-workflow-row" aria-label="Primary actions">
            <WorkflowStep
              step="1"
              title="Start an order"
              detail={`${catalog.length} catalog item${catalog.length === 1 ? "" : "s"} available`}
              to="/business/portal/projects/new"
              action="Choose packaging"
            />
            <WorkflowStep
              step="2"
              title="Review proofs"
              detail={`${quoteReviewCount} quote${quoteReviewCount === 1 ? "" : "s"} ready`}
              to="/business/portal/projects"
              action="Open projects"
            />
            <WorkflowStep
              step="3"
              title="Track or reorder"
              detail={`${orders.length} order${orders.length === 1 ? "" : "s"} in history`}
              to="/business/portal/orders"
              action="View orders"
            />
          </section>

          <section className="b2b-dashboard-flow" aria-labelledby="portal-work-heading">
            <div className="b2b-dashboard-main-column">
              <div className="b2b-section-heading">
                <div>
                  <span className="b2b-kicker">Next up</span>
                  <h2 id="portal-work-heading">Proofs and quotes</h2>
                </div>
                <Link to="/business/portal/projects">All projects</Link>
              </div>

              {activeProjects.length > 0 ? (
                <div className="b2b-card-grid">
                  {activeProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project}
                      product={productByIdFromSnapshot(portal.data, project.productId)}
                      quote={quoteForProjectFromSnapshot(portal.data, project.id)}
                    />
                  ))}
                </div>
              ) : (
                <EmptyState
                  title="No active projects"
                  detail="Start a packaging order when you are ready to create a proof."
                  action={<Link to="/business/portal/projects/new" className="b2b-primary-button">Start order</Link>}
                />
              )}
            </div>

            <aside className="b2b-dashboard-side-column">
              <section className="b2b-card">
                <span className="b2b-kicker">Workspace</span>
                <h2>Ready for orders</h2>
                <dl className="b2b-stat-list">
                  <div><dt>Catalog</dt><dd>{catalog.length}</dd></div>
                  <div><dt>Artwork</dt><dd>{brandAssets.length}</dd></div>
                  <div><dt>Projects</dt><dd>{projects.length}</dd></div>
                  <div><dt>Orders</dt><dd>{orders.length}</dd></div>
                </dl>
              </section>

              <section className="b2b-card">
                <span className="b2b-kicker">Artwork</span>
                <h2>Logos and files</h2>
                <p>Upload artwork or generate new concepts before starting an order.</p>
                <div className="b2b-action-stack">
                  <Link to="/business/portal/brands" className="b2b-secondary-button">Manage artwork</Link>
                  <Link to="/business/portal/artwork" className="b2b-secondary-button">View gallery</Link>
                </div>
              </section>
            </aside>
          </section>

          <section>
            <div className="b2b-section-heading">
              <div>
                <span className="b2b-kicker">Recent orders</span>
                <h2>History and reorders</h2>
              </div>
              <Link to="/business/portal/orders">Order history</Link>
            </div>

            {recentOrders.length > 0 ? (
              <div className="b2b-order-strip">
                {recentOrders.map((order) => {
                  const project = projects.find((item) => item.id === order.projectId);
                  const product = productByIdFromSnapshot(portal.data, project?.productId ?? "");
                  return (
                    <Link key={order.id} to="/business/portal/orders/$orderId" params={{ orderId: order.id }} className="b2b-order-summary-card">
                      <span>{order.id}</span>
                      <strong>{project?.name ?? "Packaging order"}</strong>
                      <small>{product?.title ?? "Order details"}</small>
                      <StatusBadge status={order.status} tone={order.status === "delivered" ? "good" : "warn"} />
                    </Link>
                  );
                })}
              </div>
            ) : (
              <EmptyState title="No order history yet" detail="Completed and in-progress orders will appear here." />
            )}
          </section>
        </>
      )}
    </PortalShell>
  );
}

function WorkflowStep({
  step,
  title,
  detail,
  to,
  action,
}: {
  step: string;
  title: string;
  detail: string;
  to: string;
  action: string;
}) {
  return (
    <Link to={to} className="b2b-card b2b-workflow-card">
      <span className="b2b-workflow-number">{step}</span>
      <strong>{title}</strong>
      <small>{detail}</small>
      <b>{action}</b>
    </Link>
  );
}

function EmptyState({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <section className="b2b-card b2b-empty-state">
      <h2>{title}</h2>
      <p>{detail}</p>
      {action ? <div className="b2b-action-row">{action}</div> : null}
    </section>
  );
}

function DemoNotice() {
  return (
    <section className="b2b-card b2b-demo-notice">
      <strong>Demo data enabled</strong>
      <span>Set `VITE_B2B_USE_DEMO_DATA=false` to require live business portal data.</span>
    </section>
  );
}

function LoadingGrid() {
  return (
    <section className="b2b-card-grid" aria-label="Loading business portal">
      {["Catalog", "Artwork", "Projects"].map((item) => (
        <article key={item} className="b2b-card b2b-loading-card">
          <span className="b2b-kicker">{item}</span>
          <div />
          <div />
        </article>
      ))}
    </section>
  );
}
